import { css, ThemeProvider } from '@emotion/react';
import { calculateDurationMs, formatDuration } from '@sonematic/shared';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';

import { DebugConsole } from './debug-console';
import { MicPermissionPrompt } from './mic-permission-prompt';
import { RehearsalHeader } from './rehearsal-header';
import { RehearsalResults } from './rehearsal-results';
import type { RehearsalSection } from './rehearsal-script-display';
import { RehearsalScriptDisplay } from './rehearsal-script-display';

import { useRehearsalDebug } from '@/hooks/use-rehearsal-debug';
import { useRehearsalScroll } from '@/hooks/use-rehearsal-scroll';
import { useSpeechAlignment } from '@/hooks/use-speech-alignment';
import { useTeleprompterSettings } from '@/hooks/use-teleprompter-settings';
import { saveRehearsal } from '@/lib/api-client';
import type { RehearsalEvent } from '@/lib/rehearsal/rehearsal-state-machine';
import { transition } from '@/lib/rehearsal/rehearsal-state-machine';
import type { SpeechRecognitionConstructor } from '@/lib/speech/speech-recognition-adapter';
import { calculateRealtimeWpm } from '@/lib/utils/realtime-wpm';
import { darkTheme } from '@/theme/dark-theme';
import { fontSizes, radii, shadows, spacing } from '@/theme/tokens';

export interface RehearsalViewProps {
  sections: RehearsalSection[];
  globalWpm: number;
  projectId: string;
  title?: string;
  SpeechRecognition?: SpeechRecognitionConstructor;
}

export function RehearsalView({
  sections,
  globalWpm,
  projectId,
  title,
  SpeechRecognition,
}: RehearsalViewProps) {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [state, dispatch] = useReducer(transition, 'idle');
  const [saving, setSaving] = useState(false);

  const { settings, setFontSize, setLineWidth } = useTeleprompterSettings();

  const scriptWords = useMemo(
    () => sections.flatMap((s) => s.content.split(/\s+/).filter(Boolean)),
    [sections],
  );

  const sectionBoundaries = useMemo(() => {
    const boundaries: number[] = [0];
    let wordOffset = 0;
    for (let i = 0; i < sections.length - 1; i++) {
      wordOffset += sections[i].content.split(/\s+/).filter(Boolean).length;
      boundaries.push(wordOffset);
    }
    return boundaries;
  }, [sections]);

  const sectionWordCounts = useMemo(
    () => sections.map((s) => s.content.split(/\s+/).filter(Boolean).length),
    [sections],
  );

  const speech = useSpeechAlignment(scriptWords, sectionBoundaries, { SpeechRecognition });

  const [debugOpen, setDebugOpen] = useState(false);
  const debugData = useRehearsalDebug(speech.debugRefs, debugOpen);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'd' && e.shiftKey && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setDebugOpen((prev) => !prev);
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  const scrollActive = state === 'recording' || state === 'paused';

  useRehearsalScroll({
    containerRef: scrollRef,
    sectionIndex: speech.sectionIndex,
    sectionProgress: speech.sectionProgress,
    isActive: scrollActive,
    cursorPosition: speech.cursorPosition,
  });

  const wpm = calculateRealtimeWpm(speech.cursorPosition, speech.totalDurationMs);

  function send(event: RehearsalEvent) {
    dispatch(event);
  }

  const handleStart = useCallback(() => {
    send({ type: 'START_REHEARSAL' });
  }, []);

  const handleMicGranted = useCallback(() => {
    send({ type: 'MIC_GRANTED' });
    speech.start();
  }, [speech]);

  const handleMicDenied = useCallback(() => {
    send({ type: 'MIC_DENIED' });
  }, []);

  const handlePause = useCallback(() => {
    speech.pause();
    send({ type: 'PAUSE' });
  }, [speech]);

  const handleResume = useCallback(() => {
    speech.resume();
    send({ type: 'RESUME' });
  }, [speech]);

  const handleDone = useCallback(() => {
    if (state === 'idle') {
      navigate(`/app/projects/${projectId}/edit`);
      return;
    }
    speech.stop();
    speech.finalize();
    send({ type: 'DONE' });
  }, [speech, state, navigate, projectId]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const timings = speech.sectionTimings.map((t) => ({
        position: t.position,
        durationMs: t.durationMs,
      }));
      await saveRehearsal(projectId, timings, speech.totalDurationMs);
      send({ type: 'SAVE' });
    } finally {
      setSaving(false);
    }
  }, [speech.sectionTimings, speech.totalDurationMs, projectId]);

  const handleDiscard = useCallback(() => {
    navigate(`/app/projects/${projectId}/edit`);
  }, [navigate, projectId]);

  const handleStartOver = useCallback(() => {
    speech.stop();
    speech.reset();
    send({ type: 'START_OVER' });
  }, [speech]);

  const handleRehearsalAgain = useCallback(() => {
    send({ type: 'REHEARSE_AGAIN' });
  }, []);

  // Navigate on saved
  useEffect(() => {
    if (state === 'saved') {
      navigate(`/app/projects/${projectId}/edit`);
    }
  }, [state, projectId, navigate]);

  // Browser support check
  if (!speech.isSupported) {
    return (
      <div
        css={css`
          min-height: 100vh;
          background: ${darkTheme.background};
          display: flex;
          align-items: center;
          justify-content: center;
          color: ${darkTheme.textSecondary};
          font-size: 18px;
          padding: 24px;
          text-align: center;
        `}
      >
        Speech recognition is not supported in this browser. Please use Chrome or Edge.
      </div>
    );
  }

  // Completed state → show results
  if (state === 'completed') {
    const resultSections = sections.map((s, i) => ({
      position: s.position,
      title: s.title,
      estimatedDurationMs: calculateDurationMs(sectionWordCounts[i], globalWpm),
      measuredDurationMs: speech.sectionTimings[i]?.durationMs ?? 0,
    }));

    const totalEstimatedMs = sectionWordCounts.reduce(
      (sum, wc) => sum + calculateDurationMs(wc, globalWpm),
      0,
    );

    return (
      <ThemeProvider theme={{ colors: darkTheme }}>
        <RehearsalResults
          title={title}
          totalWords={scriptWords.length}
          sections={resultSections}
          totalEstimatedMs={totalEstimatedMs}
          totalMeasuredMs={speech.totalDurationMs}
          onSave={handleSave}
          onDiscard={handleDiscard}
          onBack={handleDiscard}
          onRehearsalAgain={handleRehearsalAgain}
          saving={saving}
        />
      </ThemeProvider>
    );
  }

  const showHeader =
    state === 'idle' || state === 'requesting_mic' || state === 'recording' || state === 'paused';

  const totalEstimatedMs = sectionWordCounts.reduce(
    (sum, wc) => sum + calculateDurationMs(wc, globalWpm),
    0,
  );

  return (
    <ThemeProvider theme={{ colors: darkTheme }}>
      <div
        css={css`
          height: 100vh;
          background: ${darkTheme.background};
          display: flex;
          flex-direction: column;
          position: relative;
          overflow: hidden;
        `}
      >
        {showHeader && (
          <RehearsalHeader
            state={state}
            elapsedMs={speech.totalDurationMs}
            isListening={speech.isListening}
            wpm={wpm}
            fontSize={settings.fontSize}
            lineWidth={settings.lineWidth}
            onPause={handlePause}
            onResume={handleResume}
            onDone={handleDone}
            onStartOver={handleStartOver}
            onFontSizeChange={setFontSize}
            onLineWidthChange={setLineWidth}
            debugOpen={debugOpen}
            onDebugToggle={() => setDebugOpen((prev) => !prev)}
            positioning={debugOpen ? 'relative' : 'fixed'}
          />
        )}

        {debugOpen && debugData && (
          <DebugConsole data={debugData} onClose={() => setDebugOpen(false)} />
        )}

        {state === 'idle' ? (
          <div
            css={css`
              flex: 1;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: ${spacing.xl};
              padding-bottom: 10vh;
            `}
          >
            <div
              data-testid="rehearsal-info-card"
              css={css`
                background: linear-gradient(
                  180deg,
                  ${darkTheme.surface} 0%,
                  ${darkTheme.surfaceDeep} 100%
                );
                box-shadow:
                  0 0 0 1px rgba(106, 95, 193, 0.1),
                  ${shadows.prominent};
                border-radius: ${radii.lg};
                padding: 40px 36px;
                max-width: 440px;
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
                text-align: center;
              `}
            >
              {/* Identity group: title + metadata */}
              <h2
                css={css`
                  margin: 0;
                  font-size: ${fontSizes.xxl};
                  color: ${darkTheme.text};
                  font-weight: 700;
                `}
              >
                {title || 'Untitled Speech'}
              </h2>
              <p
                css={css`
                  margin: 6px 0 0;
                  font-size: ${fontSizes.md};
                  color: ${darkTheme.textSecondary};
                `}
              >
                {sections.length} section{sections.length !== 1 ? 's' : ''} &middot;{' '}
                {formatDuration(totalEstimatedMs)} estimated &middot;{' '}
                {scriptWords.length.toLocaleString()} words
              </p>

              {/* Instructions */}
              <p
                css={css`
                  margin: ${spacing.lg} 0 0;
                  font-size: ${fontSizes.sm};
                  color: ${darkTheme.textMuted};
                  line-height: 1.6;
                `}
              >
                Your script will appear as a teleprompter with live pacing feedback as you speak.
              </p>

              {/* Mic hint */}
              <p
                css={css`
                  margin: ${spacing.sm} 0 0;
                  font-size: ${fontSizes.sm};
                  color: ${darkTheme.textSecondary};
                  display: flex;
                  align-items: center;
                  gap: 4px;
                `}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="22" />
                </svg>
                Microphone access required
              </p>

              {/* CTA */}
              <Button
                onClick={handleStart}
                size="lg"
                css={css`
                  margin-top: ${spacing.lg};
                `}
              >
                Start Rehearsal
              </Button>
            </div>
          </div>
        ) : (
          <RehearsalScriptDisplay
            ref={scrollRef}
            sections={sections}
            currentSectionIndex={speech.sectionIndex}
            fontSize={settings.fontSize}
            cursorPosition={scrollActive ? speech.cursorPosition : undefined}
            sectionBoundaries={scrollActive ? sectionBoundaries : undefined}
            lineWidth={settings.lineWidth}
            headerOffset={showHeader && !debugOpen ? 56 : 0}
            onWordClick={debugOpen && debugData ? debugData.setCursorPosition : undefined}
          />
        )}

        {state === 'requesting_mic' && (
          <MicPermissionPrompt onGranted={handleMicGranted} onDenied={handleMicDenied} />
        )}
      </div>
    </ThemeProvider>
  );
}
